import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import UGCLayout from '@/components/layouts/UGCLayout';
import { ugcMarketplaceService } from '@/services/ugcMarketplaceService';
import { crmContactService } from '@/services/crmContactService';

const navItems = [
  { path: '/app/ugc-brand-dashboard', label: 'Dashboard', icon: 'BarChart3' },
  { path: '/app/ugc-deals', label: 'My Deals', icon: 'FileText' },
  { path: '/app/ugc-invoices-brand', label: 'Invoices', icon: 'DollarSign' },
];

const moreItems = [
  { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
];

const BrandDashboard = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const navigate = useNavigate();
  const userTier = ugcTier || profile?.ugc_tier || 'free';

  const [stats, setStats] = useState({
    activeDeals: 0,
    totalBids: 0,
    totalInvoices: 0,
    revenue: 0
  });
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBrandData();
  }, [profile?.id]);

  const loadBrandData = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const [myDeals, invoices] = await Promise.all([
        ugcMarketplaceService.listMyDeals(profile.id),
        ugcMarketplaceService.listMyInvoices(profile.id)
      ]);

      const brandDeals = myDeals.filter(d => d.brand_user_id === profile.id);
      const brandInvoices = invoices.filter(i => i.brand_user_id === profile.id);
      const totalBids = brandDeals.reduce((sum, d) => sum + (d.bids?.length || 0), 0);
      const totalRevenue = brandInvoices
        .filter(i => i.status === 'paid')
        .reduce((sum, i) => sum + (i.amount || 0), 0);

      setStats({
        activeDeals: brandDeals.filter(d => d.status === 'open').length,
        totalBids,
        totalInvoices: brandInvoices.length,
        revenue: totalRevenue
      });
      setDeals(brandDeals);
    } catch (err) {
      console.error('load brand data failed', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <UGCLayout>
      <NavigationBar items={navItems} moreItems={moreItems} hideLocks />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-100 text-xs font-semibold border border-blue-500/30 mb-3">
              <Icon name="Store" size={14} /> Brand Dashboard
            </div>
            <h1 className="text-3xl font-bold text-white">Manage your deals</h1>
            <p className="text-blue-100/80">Post deals, review bids, send invoices to creators.</p>
          </div>
          <Button onClick={() => navigate('/app/ugc-marketplace')}>
            Post a deal
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Active Deals</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.activeDeals}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Total Bids</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.totalBids}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Invoices Sent</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.totalInvoices}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Revenue</p>
            <p className="text-2xl font-bold text-white mt-1">${stats.revenue.toFixed(0)}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <button
            onClick={() => navigate('/app/ugc-marketplace')}
            className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-3 px-4 rounded-lg flex items-center gap-2 transition"
          >
            <Icon name="Plus" size={18} /> Post Deal
          </button>
          <button
            onClick={() => navigate('/app/ugc-invoices-brand')}
            className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-semibold py-3 px-4 rounded-lg flex items-center gap-2 transition"
          >
            <Icon name="DollarSign" size={18} /> View Invoices
          </button>
        </div>

        {/* Recent Deals */}
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Recent Deals</h2>
          {deals.length === 0 ? (
            <p className="text-slate-400">No deals posted yet. <button onClick={() => navigate('/app/ugc-marketplace')} className="text-blue-400 hover:underline">Post one now</button></p>
          ) : (
            <div className="space-y-3">
              {deals.map(d => (
                <div key={d.id} className="bg-slate-700/50 rounded p-3 flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-white">{d.title}</p>
                    <p className="text-sm text-slate-400">Budget: ${d.budget_min}-${d.budget_max} • Status: {d.status}</p>
                  </div>
                  <button onClick={() => navigate('/app/ugc-marketplace')} className="text-blue-400 hover:text-blue-300 text-sm">View</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </UGCLayout>
  );
};

export default BrandDashboard;
